import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function ArticleReformaLucroPresumidoReal() {
  return (
    <>
      <SEOHead
        title="Reforma Tributária: Lucro Presumido e Lucro Real - Guia Completo"
        description="Entenda as mudanças da Reforma Tributária nos regimes de Lucro Presumido e Lucro Real. Análise completa sobre IBS, CBS e impactos para empresas."
        keywords="reforma tributária, lucro presumido, lucro real, IBS, CBS, tributação empresarial"
        canonicalUrl="https://seusite.com.br/artigo/reforma-tributaria-lucro-presumido-real"
      />
      <SchemaOrg type="Article" data={{
        headline: "Reforma Tributária: Impactos no Lucro Presumido e Lucro Real",
        description: "Análise completa sobre as mudanças da Reforma Tributária nos regimes de Lucro Presumido e Lucro Real",
        author: "Equipe Tributária",
        datePublished: "2024-01-15"
      }} />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navbar />

        <article className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
              <Link to="/" className="hover:text-teal-600 transition-colors">
                <i className="ri-home-4-line"></i>
              </Link>
              <span>/</span>
              <Link to="/categoria/reforma-tributaria" className="hover:text-teal-600 transition-colors">
                Reforma Tributária
              </Link>
              <span>/</span>
              <span className="text-gray-900 font-medium">Lucro Presumido e Lucro Real</span>
            </nav>

            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="px-4 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                  Reforma Tributária
                </span>
                <span className="text-gray-500 text-sm">15 min de leitura</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Reforma Tributária: Antes e Depois do Lucro Presumido e Simples Nacional
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Entenda como a Reforma Tributária vai transformar os regimes de Lucro Presumido e Simples Nacional, e o que sua empresa precisa fazer para se adaptar às novas regras.
              </p>
              
              <div className="flex items-center gap-4 mt-8 pt-8 border-t border-gray-200">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Equipe Contabilidade Moderna</p>
                  <p className="text-sm text-gray-500">Publicado em 15 de Janeiro de 2025</p>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="mb-12 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/d45c0b251242061203a11cb2b5e5f255.jpg"
                alt="Reforma Tributária - Lucro Presumido e Simples Nacional"
                className="w-full h-[400px] object-cover object-top"
              />
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {/* Introdução */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Introdução</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  A Reforma Tributária aprovada no Brasil representa uma das maiores transformações no sistema tributário nacional das últimas décadas. Com a criação do IBS (Imposto sobre Bens e Serviços) e da CBS (Contribuição sobre Bens e Serviços), empresas de todos os portes precisam entender como essas mudanças afetarão seus regimes tributários.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Este artigo analisa especificamente o impacto da reforma nos dois regimes mais utilizados pelas empresas brasileiras: o Lucro Presumido e o Simples Nacional. Vamos explorar o cenário atual, as mudanças previstas e como sua empresa pode se preparar para essa transição.
                </p>
              </section>

              {/* Lucro Presumido - Antes */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Lucro Presumido: O Cenário Atual (Antes da Reforma)</h2>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-3">Como Funciona Hoje</h3>
                  <p className="text-blue-800">
                    O Lucro Presumido é um regime tributário simplificado para empresas com faturamento anual de até R$ 78 milhões, onde a base de cálculo do IRPJ e CSLL é determinada por presunção legal.
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Tributos Atuais no Lucro Presumido</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-file-text-line text-blue-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">IRPJ</h4>
                    <p className="text-gray-600 text-sm mb-2">Imposto de Renda Pessoa Jurídica</p>
                    <p className="text-2xl font-bold text-blue-600">15%</p>
                    <p className="text-sm text-gray-500 mt-2">+ 10% sobre lucro acima de R$ 20 mil/mês</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-calculator-line text-blue-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">CSLL</h4>
                    <p className="text-gray-600 text-sm mb-2">Contribuição Social sobre Lucro Líquido</p>
                    <p className="text-2xl font-bold text-blue-600">9%</p>
                    <p className="text-sm text-gray-500 mt-2">Sobre a base presumida</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-shopping-cart-line text-blue-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">PIS</h4>
                    <p className="text-gray-600 text-sm mb-2">Programa de Integração Social</p>
                    <p className="text-2xl font-bold text-blue-600">0,65%</p>
                    <p className="text-sm text-gray-500 mt-2">Sobre o faturamento</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-funds-line text-blue-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">COFINS</h4>
                    <p className="text-gray-600 text-sm mb-2">Contribuição para Financiamento da Seguridade Social</p>
                    <p className="text-2xl font-bold text-blue-600">3%</p>
                    <p className="text-sm text-gray-500 mt-2">Sobre o faturamento</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Características Principais</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Base de cálculo presumida (8% a 32% da receita bruta, dependendo da atividade)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Apuração trimestral do IRPJ e CSLL</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">PIS e COFINS cumulativos (sem direito a crédito)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Obrigações acessórias simplificadas</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Lucro Presumido - Depois */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Lucro Presumido: O Novo Cenário (Depois da Reforma)</h2>
                
                <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg mb-6">
                  <h3 className="text-xl font-bold text-teal-900 mb-3">As Mudanças Previstas</h3>
                  <p className="text-teal-800">
                    Com a Reforma Tributária, o PIS e COFINS serão substituídos pela CBS (Contribuição sobre Bens e Serviços), enquanto o IRPJ e CSLL permanecem inalterados. A grande mudança está na forma de tributação sobre o consumo.
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Nova Estrutura Tributária</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-file-text-line text-teal-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">IRPJ</h4>
                    <p className="text-gray-600 text-sm mb-2">Mantém as mesmas regras</p>
                    <p className="text-2xl font-bold text-teal-600">15%</p>
                    <p className="text-sm text-gray-500 mt-2">+ 10% sobre lucro acima de R$ 20 mil/mês</p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-calculator-line text-teal-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">CSLL</h4>
                    <p className="text-gray-600 text-sm mb-2">Mantém as mesmas regras</p>
                    <p className="text-2xl font-bold text-teal-600">9%</p>
                    <p className="text-sm text-gray-500 mt-2">Sobre a base presumida</p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6 md:col-span-2">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-exchange-line text-teal-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">CBS (Nova)</h4>
                    <p className="text-gray-600 text-sm mb-2">Substitui PIS e COFINS</p>
                    <p className="text-2xl font-bold text-teal-600">Alíquota Única</p>
                    <p className="text-sm text-gray-500 mt-2">Alíquota padrão estimada entre 8,8% e 9,25% (ainda em definição)</p>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
                  <h4 className="text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                    <i className="ri-alert-line"></i>
                    Principais Mudanças
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800"><strong>Não cumulatividade:</strong> Direito a crédito sobre compras e insumos</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800"><strong>Alíquota única:</strong> Mesma alíquota para todos os setores (com exceções específicas)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800"><strong>Transparência:</strong> Tributo destacado na nota fiscal</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800"><strong>Período de transição:</strong> Implementação gradual entre 2026 e 2033</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Simples Nacional - Antes */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Simples Nacional: O Cenário Atual (Antes da Reforma)</h2>
                
                <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg mb-6">
                  <h3 className="text-xl font-bold text-purple-900 mb-3">Como Funciona Hoje</h3>
                  <p className="text-purple-800">
                    O Simples Nacional é um regime tributário diferenciado e simplificado para micro e pequenas empresas com faturamento anual de até R$ 4,8 milhões, unificando oito tributos em uma única guia (DAS).
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Tributos Unificados no Simples</h3>
                
                <div className="bg-white border-2 border-purple-200 rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">8 Tributos em Uma Única Guia</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">IRPJ</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">CSLL</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">PIS</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">COFINS</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">IPI</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">ICMS</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">ISS</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-purple-600"></i>
                      </div>
                      <span className="text-gray-700">CPP (Patronal)</span>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Anexos e Alíquotas Atuais</h3>
                
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-store-line text-purple-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Anexo I</h4>
                    <p className="text-gray-600 text-sm mb-3">Comércio</p>
                    <p className="text-xl font-bold text-purple-600">4% a 19%</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-tools-line text-purple-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Anexo II</h4>
                    <p className="text-gray-600 text-sm mb-3">Indústria</p>
                    <p className="text-xl font-bold text-purple-600">4,5% a 30%</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-service-line text-purple-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Anexo III</h4>
                    <p className="text-gray-600 text-sm mb-3">Serviços</p>
                    <p className="text-xl font-bold text-purple-600">6% a 33%</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Vantagens Atuais</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Unificação de tributos em uma única guia</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Redução da carga tributária para pequenas empresas</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Simplificação das obrigações acessórias</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                      <span className="text-gray-700">Facilidade no cumprimento das obrigações fiscais</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Simples Nacional - Depois */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Simples Nacional: O Novo Cenário (Depois da Reforma)</h2>
                
                <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg mb-6">
                  <h3 className="text-xl font-bold text-teal-900 mb-3">Manutenção do Regime</h3>
                  <p className="text-teal-800">
                    A boa notícia é que o Simples Nacional será mantido! No entanto, haverá adaptações importantes na forma como os tributos sobre consumo são calculados e recolhidos.
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Mudanças na Estrutura</h3>
                
                <div className="bg-white border-2 border-teal-200 rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Nova Composição do DAS</h4>
                  <p className="text-gray-700 mb-4">
                    O ICMS e ISS serão substituídos pelo IBS (Imposto sobre Bens e Serviços), enquanto PIS e COFINS serão substituídos pela CBS. A guia DAS continuará unificada, mas com nova composição:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <span className="text-gray-700">IRPJ</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <span className="text-gray-700">CSLL</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-star-line text-teal-600"></i>
                      </div>
                      <span className="text-gray-700"><strong>CBS (Nova)</strong></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-star-line text-teal-600"></i>
                      </div>
                      <span className="text-gray-700"><strong>IBS (Novo)</strong></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <span className="text-gray-700">CPP (Patronal)</span>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-percent-line text-teal-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">Alíquotas Reduzidas</h4>
                    <p className="text-gray-700 text-sm">
                      Empresas do Simples terão desconto de até 30% nas alíquotas do IBS e CBS em relação ao regime geral, mantendo a vantagem competitiva.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-file-list-line text-teal-600 text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">Simplificação Mantida</h4>
                    <p className="text-gray-700 text-sm">
                      A simplicidade do regime será preservada, com guia única e obrigações acessórias reduzidas, mesmo com os novos tributos.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                  <h4 className="text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line"></i>
                    Pontos de Atenção
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800">Empresas do Simples poderão gerar créditos de IBS e CBS para seus clientes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800">Necessidade de adaptação dos sistemas de emissão de notas fiscais</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800">Período de transição gradual entre 2026 e 2033</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-line text-amber-600 text-xl mt-1"></i>
                      <span className="text-amber-800">Possível necessidade de revisão do planejamento tributário</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Comparativo */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Comparativo: Antes x Depois</h2>
                
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Lucro Presumido</h3>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-xl p-6 border-2 border-blue-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <i className="ri-time-line text-blue-600 text-xl"></i>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">ANTES</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">PIS: 0,65%</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">COFINS: 3%</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Regime cumulativo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Sem direito a crédito</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-blue-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Total: 3,65% sobre faturamento</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-xl p-6 border-2 border-teal-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <i className="ri-flashlight-line text-teal-600 text-xl"></i>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">DEPOIS</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">CBS: ~8,8% a 9,25%</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Regime não cumulativo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Direito a crédito sobre compras</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Alíquota única nacional</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Transparência na nota fiscal</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Simples Nacional</h3>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-xl p-6 border-2 border-purple-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <i className="ri-time-line text-purple-600 text-xl"></i>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">ANTES</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-purple-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">8 tributos unificados</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-purple-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">ICMS e ISS inclusos</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-purple-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">PIS e COFINS inclusos</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-purple-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Sem geração de créditos</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-purple-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Alíquotas progressivas por anexo</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-xl p-6 border-2 border-teal-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <i className="ri-flashlight-line text-teal-600 text-xl"></i>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">DEPOIS</h4>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Regime mantido e simplificado</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">IBS substitui ICMS e ISS</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">CBS substitui PIS e COFINS</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Geração de créditos para clientes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5"></i>
                          <span className="text-gray-700 text-sm">Desconto de até 30% nas alíquotas</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Período de Transição */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Período de Transição (2026-2033)</h2>
                
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Implementação Gradual</h3>
                  <p className="text-gray-700 mb-6">
                    A Reforma Tributária não será implementada de uma vez. Haverá um período de transição de 7 anos para que empresas e governos se adaptem às novas regras.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-5 border-l-4 border-teal-500">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-teal-600">2026</span>
                        <span className="text-gray-600">•</span>
                        <span className="font-semibold text-gray-900">Teste do Sistema</span>
                      </div>
                      <p className="text-gray-700 text-sm">Alíquota de teste de 1% para IBS e CBS, mantendo tributos atuais</p>
                    </div>

                    <div className="bg-white rounded-xl p-5 border-l-4 border-teal-500">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-teal-600">2027</span>
                        <span className="text-gray-600">•</span>
                        <span className="font-semibold text-gray-900">Início da Transição</span>
                      </div>
                      <p className="text-gray-700 text-sm">CBS começa a substituir PIS e COFINS gradualmente</p>
                    </div>

                    <div className="bg-white rounded-xl p-5 border-l-4 border-blue-500">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-blue-600">2029</span>
                        <span className="text-gray-600">•</span>
                        <span className="font-semibold text-gray-900">Extinção do PIS/COFINS</span>
                      </div>
                      <p className="text-gray-700 text-sm">CBS assume completamente o lugar do PIS e COFINS</p>
                    </div>

                    <div className="bg-white rounded-xl p-5 border-l-4 border-blue-500">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-blue-600">2033</span>
                        <span className="text-gray-600">•</span>
                        <span className="font-semibold text-gray-900">Implementação Completa</span>
                      </div>
                      <p className="text-gray-700 text-sm">IBS substitui totalmente ICMS e ISS - reforma 100% implementada</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Como se Preparar */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Como Sua Empresa Deve se Preparar</h2>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-book-open-line text-teal-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">1. Educação e Capacitação</h3>
                    <p className="text-gray-700 text-sm">
                      Invista em treinamento para sua equipe contábil e financeira. Entenda profundamente as mudanças e como elas afetam seu negócio.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-computer-line text-teal-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">2. Atualização de Sistemas</h3>
                    <p className="text-gray-700 text-sm">
                      Certifique-se de que seus sistemas de gestão e emissão de notas fiscais estejam preparados para os novos tributos e regras de crédito.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-line-chart-line text-teal-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">3. Planejamento Tributário</h3>
                    <p className="text-gray-700 text-sm">
                      Reavalie seu planejamento tributário. A não cumulatividade pode trazer vantagens ou desvantagens dependendo do seu modelo de negócio.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-file-list-3-line text-teal-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">4. Revisão de Processos</h3>
                    <p className="text-gray-700 text-sm">
                      Revise seus processos internos de compras, vendas e controle de estoque para aproveitar os créditos tributários disponíveis.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-team-line text-teal-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">5. Consultoria Especializada</h3>
                    <p className="text-gray-700 text-sm">
                      Conte com o apoio de profissionais especializados em Reforma Tributária para garantir uma transição tranquila e eficiente.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-calendar-check-line text-teal-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">6. Acompanhamento Contínuo</h3>
                    <p className="text-gray-700 text-sm">
                      Mantenha-se atualizado sobre regulamentações e ajustes que serão publicados durante o período de transição.
                    </p>
                  </div>
                </div>
              </section>

              {/* Conclusão */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusão</h2>
                
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    A Reforma Tributária representa uma mudança histórica no sistema tributário brasileiro, trazendo tanto desafios quanto oportunidades para empresas do Lucro Presumido e Simples Nacional.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Para o <strong>Lucro Presumido</strong>, a principal mudança está na substituição do PIS e COFINS pela CBS, com a vantagem da não cumulatividade e direito a créditos. Embora a alíquota nominal seja maior, o impacto efetivo dependerá da cadeia produtiva e da capacidade de aproveitamento de créditos.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Para o <strong>Simples Nacional</strong>, a boa notícia é a manutenção do regime com suas vantagens de simplicidade e alíquotas reduzidas. A substituição do ICMS/ISS pelo IBS e do PIS/COFINS pela CBS será feita de forma transparente, mantendo a guia única e adicionando a possibilidade de geração de créditos.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    O período de transição de 7 anos oferece tempo suficiente para adaptação, mas é fundamental começar a se preparar desde já. Empresas que se anteciparem e planejarem adequadamente estarão em posição vantajosa para aproveitar as oportunidades e minimizar os impactos negativos da reforma.
                  </p>
                </div>
              </section>

              {/* CTA */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-center text-white mb-12">
                <h3 className="text-2xl font-bold mb-4">Precisa de Ajuda com a Reforma Tributária?</h3>
                <p className="text-teal-50 mb-6 max-w-2xl mx-auto">
                  Nossa equipe de especialistas está pronta para ajudar sua empresa a navegar pelas mudanças da Reforma Tributária e garantir a melhor estratégia para o seu negócio.
                </p>
                <Link
                  to="/contato"
                  className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap"
                >
                  Fale com um Especialista
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Related Articles */}
            <div className="border-t border-gray-200 pt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Artigos Relacionados</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/artigo/reforma-tributaria-consumo" className="group">
                  <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 transition-colors hover:shadow-lg">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src="https://readdy.ai/api/search-image?query=modern%20shopping%20cart%20with%20tax%20documents%20and%20calculator%20on%20desk%2C%20professional%20business%20taxation%20concept%2C%20clean%20bright%20office%20environment%20with%20financial%20charts%20and%20graphs%2C%20high%20quality%20business%20photography&width=400&height=300&seq=reform-consumption-tax-001&orientation=landscape"
                        alt="Reforma Tributária sobre Consumo"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        Reforma Tributária sobre Consumo
                      </h4>
                      <p className="text-sm text-gray-600">
                        Entenda as mudanças na tributação sobre consumo
                      </p>
                    </div>
                  </div>
                </Link>

                <Link to="/artigo/planejamento-tributario" className="group">
                  <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 transition-colors hover:shadow-lg">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src="https://readdy.ai/api/search-image?query=business%20professional%20analyzing%20financial%20documents%20and%20tax%20planning%20strategies%20on%20laptop%2C%20modern%20office%20desk%20with%20charts%20and%20reports%2C%20strategic%20planning%20concept%2C%20professional%20corporate%20photography&width=400&height=300&seq=tax-planning-strategy-001&orientation=landscape"
                        alt="Planejamento Tributário"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        Planejamento Tributário
                      </h4>
                      <p className="text-sm text-gray-600">
                        Estratégias para otimizar sua carga tributária
                      </p>
                    </div>
                  </div>
                </Link>

                <Link to="/artigo/esocial-2026" className="group">
                  <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 transition-colors hover:shadow-lg">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src="https://readdy.ai/api/search-image?query=digital%20HR%20system%20interface%20on%20computer%20screen%20showing%20employee%20data%20and%20payroll%20management%2C%20modern%20workplace%20technology%2C%20professional%20business%20software%20concept%2C%20clean%20office%20environment&width=400&height=300&seq=esocial-system-2026-001&orientation=landscape"
                        alt="eSocial 2026"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        eSocial 2026
                      </h4>
                      <p className="text-sm text-gray-600">
                        Prepare-se para as mudanças do eSocial
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}